/**
 * The organization's workflows, as a page (§5.25, §5.15).
 *
 * The canvas below is the same one the workspace editor used to hold; what is
 * here is everything around it — reading the version, publishing against it,
 * and the two refusals that answer: a document the Worker would not keep, and
 * somebody else's publish since this page was opened.
 *
 * It is the settings page's shape rather than the workspace editor's, because
 * that is what this document is now: one list per organization, versioned on
 * its own, read by every install and written where somebody is signed in.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { HeaderButton } from '../../../components/Shell';
import { Note, PageBody } from '../../../components/list';
import { color, font } from '../../../theme/tokens';
import { Empty, RowAction } from '../../../components/primitives';
import { useConfirm } from '../../../components/Modal';
import { api, ifMatch, apiPath } from '../../../lib/api';
import type { DocWorkflow, Organization, OrgWorkflowsConflict, OrgWorkflowsRecord } from '../../../lib/types';
import { ProblemList, actorLabel, conflictOf, failureText, refusalProblems, stamp } from '../shared';
import { Workflows } from './Workflows';

export function OrgWorkflowsPage({
  organization,
  admin
}: {
  organization: Organization | null;
  /** An owner or an administrator writes them; a member reads what their tasks run. */
  admin: boolean;
}): React.JSX.Element {
  const [record, setRecord] = useState<OrgWorkflowsRecord | null>(null);
  const [draft, setDraft] = useState<DocWorkflow[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [dialog, ask] = useConfirm();
  const [conflict, setConflict] = useState<OrgWorkflowsConflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const orgId = organization?.id ?? null;

  /** Take their version, losing what is on this page. Asked first, because
   *  nothing on screen has been published and there is no undo for it. */
  const reloadTheirs = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Reload their version?',
      lede: 'Your edits on this page are dropped, and theirs is what you carry on from.',
      confirm: 'Reload theirs',
      danger: true
    });
    if (agreed) void load();
  };

  const load = useCallback(async (): Promise<void> => {
    if (!orgId) return;
    const answer = await api.get<OrgWorkflowsRecord>(apiPath`/orgs/${orgId}/workflows`);
    setRecord(answer);
    setDraft(answer.document.workflows);
    setProblems([]);
    setConflict(null);
  }, [orgId]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'These workflows could not be read.')));
  }, [load]);

  if (!organization) {
    return (
      <PageBody>
        <Empty title="No organization">Workflows are shared by an organization, and this account holds no seat in one.</Empty>
      </PageBody>
    );
  }
  if (!record) {
    return (
      <PageBody>
        {error ? <Empty title="These workflows could not be read">{error}</Empty> : <Empty title="Reading the workflows…" />}
      </PageBody>
    );
  }

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNote(null);
    setProblems([]);
    setConflict(null);
    try {
      const saved = await api.send<OrgWorkflowsRecord>(
        'PUT',
        apiPath`/orgs/${organization.id}/workflows`,
        { document: { workflows: draft } },
        ifMatch(record.version)
      );
      await load();
      setNote(`Published version ${saved.version}. Members’ installs read it at their next check.`);
    } catch (failure) {
      const refused = refusalProblems(failure);
      const lost = conflictOf<OrgWorkflowsConflict>(failure);
      if (refused) setProblems(refused);
      else if (lost) setConflict(lost);
      else setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  };

  const next = record.version + 1;
  // How many workflows the draft differs from what is published in — changed,
  // added or removed — which is what the bar counts (§13, §44.1).
  const published = new Map(record.document.workflows.map((one) => [one.id, JSON.stringify(one)]));
  const pending =
    draft.filter((one) => published.get(one.id) !== JSON.stringify(one)).length +
    record.document.workflows.filter((one) => !draft.some((kept) => kept.id === one.id)).length;

  return (
    <>
      {dialog}
      {/* The publish bar (redesign §13, §44.1), the one the settings have:
          sticky under the header on the frosted glass, the draft's count on
          the left, Discard just left of Publish; a reader gets the read band. */}
      <div
        role="region"
        aria-label="Draft"
        style={{
          position: 'sticky',
          top: 52,
          zIndex: 9,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 52,
          margin: '16px 0 0',
          padding: '0 var(--al-gutter)',
          background: admin ? 'rgba(255,255,255,.8)' : color.surfaceAlt,
          backdropFilter: admin ? 'saturate(180%) blur(20px)' : undefined,
          borderBottom: `1px solid ${color.line}`
        }}
      >
        {admin ? (
          <>
            {pending > 0 ? (
              <span aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: color.warnText }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: '#C9A03A' }} />
                {pending === 1 ? '1 change, not published' : `${pending} changes, not published`}
              </span>
            ) : (
              <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted }}>
                v{record.version} published · one list for the whole organization
              </span>
            )}
            <span style={{ flex: 1 }} />
            {pending > 0 && !busy && <HeaderButton onClick={() => setDraft(record.document.workflows)}>Discard</HeaderButton>}
            <HeaderButton
              primary
              onClick={() => void save()}
              disabled={busy}
              reason={!busy && pending === 0 ? 'Nothing to publish yet' : undefined}
            >
              {busy ? 'Publishing' : `Publish v${next}`}
            </HeaderButton>
          </>
        ) : (
          <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted }}>
            v{record.version} · what your installs are handed
          </span>
        )}
      </div>

      {(error ?? note ?? conflict ?? problems.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px var(--al-gutter) 0' }}>
          {error && <Note tone="warn">{error}</Note>}
          {note && <Note>{note}</Note>}
          {problems.length > 0 && <ProblemList problems={problems} />}
          {conflict && (
            <Note tone="warn">
              <strong style={{ fontWeight: 600 }}>Somebody published since you opened this.</strong> Version{' '}
              {conflict.current.version} was published {stamp(conflict.current.updatedAt)} by{' '}
              {actorLabel(conflict.current.updatedBy)}. Reloading takes their list; keeping yours leaves this page as it
              is, and the next publish will be refused again.
              <div style={{ display: 'flex', gap: 14, marginTop: 12 }}>
                <RowAction
                  onClick={() => void reloadTheirs()}
                >
                  Reload their version
                </RowAction>
                <RowAction onClick={() => setConflict(null)}>Keep editing</RowAction>
              </div>
            </Note>
          )}
        </div>
      )}

      <Workflows workflows={draft} editable={admin} onChange={setDraft} />
    </>
  );
}
